import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ToolTypes } from 'src/app/models/ToolTypes.model';

/**
 * The slice of configurator state that lives on NavbarComponent rather than in
 * ProductTabsService — slider positions, the rule-derived material bounds, and
 * the per-category tool-type picks.
 */
export interface NavbarConfiguratorState {
  /** Raw slider values, keyed by material id. */
  materialValues: { [key: string]: number };
  /** Bounds after material rules have been applied. */
  affectedMaterialValues: {
    [key: number]: {
      min: number;
      max: number;
      categories: number[];
      affectedByRule: boolean;
      affectedByRuleId: number | null;
    };
  };
  /** Static bounds straight off the material definitions. */
  materialLimits: { [key: number]: { min: number; max: number; categories: number[] } };
  /** The material-value objects as they are handed to ProductTabsService. */
  aggregatedMaterialValues: {
    [key: number]: { Name: string; Value: string; card_place_id: number; id: number };
  };
  selectedToolTypesByCategory: { [categoryId: number]: ToolTypes };
  selectedTabIndex: number;
}

const EMPTY_STATE: NavbarConfiguratorState = {
  materialValues: {},
  affectedMaterialValues: {},
  materialLimits: {},
  aggregatedMaterialValues: {},
  selectedToolTypesByCategory: {},
  selectedTabIndex: 0,
};

/**
 * Keeps a copy of NavbarComponent's local configurator state at application
 * scope.
 *
 * NavbarComponent is instantiated three times — in the configurator, in
 * checkout and in order-details — so the configurator's instance is destroyed
 * the moment the user leaves for checkout. By then its local state is gone,
 * and that is exactly when a configuration needs to be snapshotted into the
 * cart. The navbar publishes here on every mutation so the state outlives it.
 */
@Injectable({ providedIn: 'root' })
export class ConfiguratorStateService {
  private state: NavbarConfiguratorState = { ...EMPTY_STATE };

  private readonly resetRequested = new Subject<void>();

  /**
   * Emitted when the mirror is cleared. A live NavbarComponent listens and
   * clears its own locals, so the reset service never needs a reference to
   * the component.
   */
  get navbarResetRequested$(): Observable<void> {
    return this.resetRequested.asObservable();
  }

  publishNavbarState(partial: Partial<NavbarConfiguratorState>): void {
    this.state = { ...this.state, ...partial };
  }

  /**
   * A deep-enough copy that later navbar mutations cannot reach back into a
   * snapshot already handed to the cart.
   */
  get currentNavbarState(): NavbarConfiguratorState {
    return JSON.parse(JSON.stringify(this.state)) as NavbarConfiguratorState;
  }

  clear(): void {
    this.state = { ...EMPTY_STATE };
    this.resetRequested.next();
  }
}
